import * as React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoIcon from '@mui/icons-material/Info';
import DownloadIcon from '@mui/icons-material/Download';
import TicketService from '../../../services/ticket.service';

const DEFAULT_FILTERS = {
  search: '',
  number: '',
  order_number: '',
  client: '',
  purchase_date_from: '',
  purchase_date_to: '',
  validation_status: '',
  validation_date_from: '',
  validation_date_to: '',
};

const getClientLabel = (client) => {
  if (!client) return 'N/A';
  if (typeof client === 'string') return client;
  const fullname =
    client.fullName ||
    client.fullname ||
    `${client.firstName || client.firstname || ''} ${client.lastName || client.lastname || ''}`.trim();
  return fullname || client.email || client.phone || 'N/A';
};

const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleString();
};

const getItemsFromResponse = (response) => {
  const candidates = response?.ordered_tickets || response?.tickets || response?.items || response?.data || response;

  if (Array.isArray(candidates)) return candidates;

  if (candidates && typeof candidates === 'object') {
    return Object.values(candidates).filter((value) => value && typeof value === 'object');
  }

  return [];
};

const getStatusChipColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'used':
      return 'success';
    case 'valid':
      return 'primary';
    default:
      return 'default';
  }
};

const getTicketDownloadUrl = (ticketRow) => {
  const rawUrl = ticketRow?.url || ticketRow?.urlTicket;
  if (!rawUrl) return null;
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return rawUrl;
  return `${process.env.REACT_APP_BASE_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
};

export default function List() {
  const [rows, setRows] = React.useState([]);
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [selectedTicket, setSelectedTicket] = React.useState(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const fetchOrderedTickets = React.useCallback(
    async (nextPage = page, nextRowsPerPage = rowsPerPage, customFilters = filters) => {
      setLoading(true);
      try {
        const response = await TicketService.listOrderedTickets({
          page: nextPage + 1,
          limit: nextRowsPerPage,
          ...customFilters,
        });

        const items = getItemsFromResponse(response);

        const pagination = response?.pagination || {};
        const totalItems = pagination.total ?? response?.total ?? (Array.isArray(items) ? items.length : 0);

        setRows(Array.isArray(items) ? items : []);
        setTotal(totalItems);
      } catch (error) {
        setRows([]);
        setTotal(0);
        setSnackbar({
          open: true,
          message: error.message || 'Erreur lors du chargement des tickets générés',
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
    [filters, page, rowsPerPage]
  );

  React.useEffect(() => {
    fetchOrderedTickets(0, rowsPerPage, filters);
  }, []);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = async () => {
    setPage(0);
    await fetchOrderedTickets(0, rowsPerPage, filters);
  };

  const handleResetFilters = async () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
    await fetchOrderedTickets(0, rowsPerPage, DEFAULT_FILTERS);
  };

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    await fetchOrderedTickets(newPage, rowsPerPage, filters);
  };

  const handleChangeRowsPerPage = async (event) => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(nextRowsPerPage);
    setPage(0);
    await fetchOrderedTickets(0, nextRowsPerPage, filters);
  };

  const handleOpenDetails = (ticketRow) => {
    setSelectedTicket(ticketRow);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedTicket(null);
  };

  const handleDownloadTicket = (ticketRow) => {
    const downloadUrl = getTicketDownloadUrl(ticketRow);
    if (!downloadUrl) {
      setSnackbar({
        open: true,
        message: 'Aucun lien de téléchargement disponible pour ce ticket',
        severity: 'warning',
      });
      return;
    }
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa' }}>
      <Paper
        sx={{
          width: '100%',
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        }}
      >
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontSize: '20px', fontWeight: 'bold' }}>
            Tickets générés
          </Typography>
        </Toolbar>

        <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                name="search"
                label="Recherche globale"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="N° ticket, commande, nom, email, téléphone..."
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                name="number"
                label="N° ticket"
                value={filters.number}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                name="order_number"
                label="N° commande"
                value={filters.order_number}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                name="client"
                label="Client"
                value={filters.client}
                onChange={handleFilterChange}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                name="purchase_date_from"
                type="date"
                label="Achat du"
                value={filters.purchase_date_from}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                name="purchase_date_to"
                type="date"
                label="Achat au"
                value={filters.purchase_date_to}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Statut validation</InputLabel>
                <Select
                  name="validation_status"
                  label="Statut validation"
                  value={filters.validation_status}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="used">used</MenuItem>
                  <MenuItem value="valid">valid</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                name="validation_date_from"
                type="date"
                label="Validation du"
                value={filters.validation_date_from}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                name="validation_date_to"
                type="date"
                label="Validation au"
                value={filters.validation_date_to}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={handleResetFilters}>
                Réinitialiser
              </Button>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleApplyFilters}>
                Rechercher
              </Button>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 1100 }}>
            <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
              <TableRow>
                <TableCell>N° ticket</TableCell>
                <TableCell>N° commande</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Ticket</TableCell>
                <TableCell>Date achat</TableCell>
                <TableCell>Statut validation</TableCell>
                <TableCell>Date validation</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow hover key={row.id || `${row.number}-${row.order?.id || row.order_id || 'order'}`}>
                  <TableCell>{row.number || 'N/A'}</TableCell>
                  <TableCell>{row.order?.number || row.order_number || 'N/A'}</TableCell>
                  <TableCell>{row.client?.email || getClientLabel(row.client)}</TableCell>
                  <TableCell>{row.ticket?.name || 'N/A'}</TableCell>
                  <TableCell>{formatDate(row.orderedAt || row.purchase_date || row.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.validation?.status || row.validation_status || row.statut || 'N/A'}
                      color={getStatusChipColor(row.validation?.status || row.validation_status || row.statut)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(row.validation?.dateVerif || row.validation_date)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="outlined" startIcon={<InfoIcon />} onClick={() => handleOpenDetails(row)}>
                      Détails
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Aucun ticket généré trouvé
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Détails du ticket</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Numéro ticket</Typography>
              <Typography variant="body1">{selectedTicket?.number || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Numéro commande</Typography>
              <Typography variant="body1">{selectedTicket?.order?.number || selectedTicket?.order_number || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Client</Typography>
              <Typography variant="body1">{getClientLabel(selectedTicket?.client)}</Typography>
              <Typography variant="body2">{selectedTicket?.client?.email || 'N/A'}</Typography>
              <Typography variant="body2">{selectedTicket?.client?.phone || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Ticket</Typography>
              <Typography variant="body1">{selectedTicket?.ticket?.name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Date achat</Typography>
              <Typography variant="body1">{formatDate(selectedTicket?.orderedAt || selectedTicket?.purchase_date || selectedTicket?.createdAt)}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Statut validation</Typography>
              <Chip
                label={selectedTicket?.validation?.status || selectedTicket?.validation_status || selectedTicket?.statut || 'N/A'}
                color={getStatusChipColor(selectedTicket?.validation?.status || selectedTicket?.validation_status || selectedTicket?.statut)}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">Date validation</Typography>
              <Typography variant="body1">{formatDate(selectedTicket?.validation?.dateVerif || selectedTicket?.validation_date)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Activité de validation</Typography>
              <Typography variant="body1">{selectedTicket?.validation?.activity?.name || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">Paiement</Typography>
              <Typography variant="body1">
                {selectedTicket?.payment?.paymentMethod || 'N/A'} - {selectedTicket?.payment?.operator || 'N/A'} - {selectedTicket?.payment?.status || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Fermer</Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownloadTicket(selectedTicket)}
          >
            Télécharger le ticket
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
